import { formatDistanceToNowStrict } from "date-fns";

// Campaign currency (set at campaign creation, immutable). Keep in sync with
// backend/utils/currency.py and lambdas/outreach_lambda/utils/currency.py.
// Unrelated to Stripe billing currencies (types/plans.ts PlanPrice).
export const CURRENCY_CODES = ["USD", "EUR", "GBP"] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];
export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
};

export function currencySymbol(code?: string | null): string {
  return CURRENCY_SYMBOLS[(code ?? "USD") as CurrencyCode] ?? "$";
}

export function formatCurrency(
  value: number | string | null | undefined,
  code?: string | null,
): string {
  if (value === null || value === undefined || value === "") return "";
  const amount = typeof value === "number" ? value.toLocaleString("en-US", { maximumFractionDigits: 2 }) : value;
  return `${currencySymbol(code)}${amount}`;
}

const COMPACT = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

/** Followers, views, likes: 12345 → "12.3K", 2500000 → "2.5M", 523.7 → "523". */
export function formatCompact(n: number | null | undefined, fallback = "N/A"): string {
  if (typeof n !== "number" || Number.isNaN(n)) return fallback;
  return Math.abs(n) < 1000 ? Math.trunc(n).toLocaleString("en-US") : COMPACT.format(n);
}

/** "just now", "5 minutes ago", "3 months ago"; "" when unset. */
export function timeAgo(at: string | number | Date | null | undefined): string {
  if (!at) return "";
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return String(at);
  if (Date.now() - date.getTime() < 60_000) return "just now";
  return formatDistanceToNowStrict(date, { addSuffix: true });
}
